import { Constants, ProcessStatus, redirect } from '@dx-ui/dx-common';
import compose from 'lodash/flowRight';
import PropTypes from 'prop-types';
import { linkToRecord, Translate } from 'ra-core';
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataProvider,
  hideNotification,
  MenuItemLink,
  showNotification,
  translate,
  withDataProvider,
} from 'react-admin';
import { connect } from 'react-redux';
import {
  AlfrescoDocumentService,
  CopyOperation,
  DataHelpers,
  DespatchAdviceService,
  DespatchAdviceServiceCreator,
  DocumentServiceFactory,
} from '../../services';
import ToolbarButton from '../../shared/ToolbarButton';
import {
  AlfrescoContent,
  HideNotification,
  P2pData,
  Redirect,
  ShowNotification,
} from '../../shared/types';

interface DespatchAdviceActionProps {
  record: P2pData;
  translate: Translate;
  dataProvider: DataProvider;
  isMenuItem: boolean;
  permissions: any;
  redirect: Redirect;
  showNotification: ShowNotification;
  hideNotification: HideNotification;
}

const DespatchAdviceActions = (props: DespatchAdviceActionProps) => {
  const {
    record,
    translate,
    dataProvider,
    isMenuItem,
    permissions,
    redirect,
    showNotification,
    hideNotification,
  } = props;

  const processStatus: ProcessStatus = DataHelpers.getProcessStatus(record);
  const isWebDespatchAdvice = AlfrescoDocumentService.isWebDocument(record);

  const [copyPermissions, setCopyPermissions] = useState<{
    canClone: boolean;
  }>({
    canClone: false,
  });

  const [loading, setLoading] = useState<boolean>(false);

  const documentService = useMemo(
    () => new AlfrescoDocumentService(dataProvider),
    [dataProvider]
  );

  const desadvService = useMemo(
    () =>
      DocumentServiceFactory.create(
        DespatchAdviceServiceCreator,
        dataProvider
      ) as DespatchAdviceService,
    [dataProvider]
  );

  useEffect(() => {
    const checkForPermissions = async () => {
      let canClone = false;

      setLoading(true);
      try {
        const canCreate = DespatchAdviceService.canCreate(permissions);

        if (canCreate) {
          const recipientId = DataHelpers.getRecipientId(record);
          const hasTemplateRights = await desadvService.hasDesadvTemplate(
            recipientId
          );

          const clonePermissions = await documentService.getClonePermissions();

          const cloneKey = isWebDespatchAdvice
            ? CopyOperation.CAN_CLONE_WEB_DESADV
            : CopyOperation.CAN_CLONE_CLASSIC_DESADV;
          canClone =
            isWebDespatchAdvice &&
            hasTemplateRights &&
            !!clonePermissions[cloneKey]?.includes(processStatus);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      setCopyPermissions({
        canClone,
      });

      setLoading(false);
    };

    checkForPermissions();
  }, [
    dataProvider,
    processStatus,
    isWebDespatchAdvice,
    record,
    permissions,
    desadvService,
    documentService,
  ]);

  enum DespatchAdviceActionType {
    CLONE,
  }

  const onClickDoAction = (actionType: DespatchAdviceActionType) => {
    return async (e) => {
      e.preventDefault();

      if (!record.id) {
        throw new Error('record id cannot be null');
      }

      setLoading(true);
      showNotification('ra.page.loading');

      try {
        let result: AlfrescoContent;
        switch (actionType) {
          case DespatchAdviceActionType.CLONE:
            result = await desadvService.clone(record.id);
            break;
          default:
            throw new Error(`Invalid value for actionType: ${actionType}`);
        }
        hideNotification();
        setLoading(false);
        redirect(
          `/${linkToRecord(Constants.RESOURCE_WEBDESPATCH_ADVICE, result.id)}`
        );
      } catch (e) {
        hideNotification();
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
  };

  if (isMenuItem) {
    const sanitizeLinkProps = ({
      isMenuItem,
      translate,
      record,
      dataProvider,
      dispatch,
      showNotification,
      hideNotification,
      redirect,
      ...rest
    }: any) => rest;
    return (
      <>
        {copyPermissions.canClone && (
          <MenuItemLink
            disabled={loading}
            to={``}
            primaryText={translate('dxMessages.buttons.cloneDespatchAdvice')}
            onClick={onClickDoAction(DespatchAdviceActionType.CLONE)}
            {...sanitizeLinkProps(props)}
          />
        )}
      </>
    );
  } else {
    const sanitizeBtnProps = ({
      dispatch,
      dataProvider,
      isMenuItem,
      classes,
      className,
      redirect,
      showNotification,
      hideNotification,
      ...rest
    }: any) => rest;
    return (
      <>
        {copyPermissions.canClone && (
          <ToolbarButton
            disabled={loading}
            label={'dxMessages.buttons.cloneDespatchAdvice'}
            {...sanitizeBtnProps(props)}
            onClick={onClickDoAction(DespatchAdviceActionType.CLONE)}
          />
        )}
      </>
    );
  }
};

DespatchAdviceActions.propTypes = {
  record: PropTypes.any.isRequired,
  basePath: PropTypes.string.isRequired,
  permissions: PropTypes.any,
};

DespatchAdviceActions.defaultProps = {
  isMenuItem: true,
};

const enhance = compose(
  withDataProvider,
  translate,
  connect(undefined, {
    showNotification,
    hideNotification,
    redirect,
  })
);

export default enhance(DespatchAdviceActions);
